import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Typography,
  Grid,
  Box,
  useMediaQuery,
  Skeleton,
  Button,
  Paper,
  Avatar,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  IconButton,
  AccordionDetails,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import CommonButton from "../../Components/InputComponents/CommonButton";
import { decryptId } from "../../Components/CryptoComponent";
import { Add, ExpandMore } from "@mui/icons-material";
import { axiosAPI } from "../../Components/AxiosComponents/Axios";
import SideStageCard from "../../Views/Candidate/Components/SideStageCard";
import HorizontalStageBox from "../../CandidateView/Dashboard/Components/HorizontalStageBox";
import SelectInput from "../../Components/InputComponents/SelectInput";

const calculateAge = (dob) => {
  if (!dob) return "";

  const birthDate = new Date(dob);
  if (isNaN(birthDate.getTime())) return "";

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();

  const monthDiff = today.getMonth() - birthDate.getMonth();
  const dayDiff = today.getDate() - birthDate.getDate();

  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age--;
  }

  return age >= 0 ? age : "";
};

const ClientCandidateProfile = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const fromLocation = location?.state?.from;

  const paginationState = location?.state?.pagination || {
    page: 0,
    rowsPerPage: 25,
  };

  const filterData = location?.state?.filterData || {
    name: "",
    profession: "",
    jobapplication: "",
    jobid: "",
    interviewdate: "",
  };

  const candidateData = location?.state?.selectedId;

  const [personalDetails, setPersonalDetails] = useState({
    Name: "",
    Alias: "",
    Date_Of_Birth: null,
    Age: "",
    Passport_Number: "",
    Address: "",
    Photo: "",
  });

  const [experienceDetails, setExperienceDetails] = useState({
    Designation: "",
    Department_1: "",
    Department_2: "",
    Department_3: "",
    Registration_License: "",
    Registration_Expiry: null,
    last_Working_Date: "",
    Profession: "",
    isMedical: "",
    Years_of_Experience: "",
    Career_Gap: "",
    Gap_Details: "",
  });

  const [selectedCandidateId, setCandidateId] = useState("");
  const [selectedJobListId, setSelectedJobListId] = useState("");

  const [expanded, setExpanded] = useState(true); // Accordion state
  const [selectedProfileTab, setSelectedProfileTab] = useState(0);
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [procedureData, setProcedureData] = useState(null);

  const handleChange = (event) => {
    setSelectedJobListId(event);
  };

  const getJobData = async () => {
    if (!selectedJobListId || !selectedCandidateId) {
      setProcedureData([]);
      return;
    }
    try {
      const res = await axiosAPI?.post(`/clientportal/JobData`, {
        JobApplicationId: selectedJobListId,
        CandidateId: selectedCandidateId,
      });
      if (res?.data?.data) {
        const rawValue = res?.data?.data?.[0]?.CRD_FieldValue;

        if (typeof rawValue === "string") {
          try {
            const parsed = JSON.parse(rawValue);
            setProcedureData(parsed);
          } catch (error) {
            console.error("Invalid JSON in CRD_FieldValue", error);
            setProcedureData(null);
          }
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getJobData();
  }, [selectedJobListId]);

  const handleAccordionToggle = () => {
    setExpanded((prev) => !prev);
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!candidateData || typeof candidateData !== "string") {
      navigate("/Candidates");
      return;
    }

    let data;
    try {
      data = JSON.parse(candidateData);
    } catch {
      navigate("/Candidates");
      return;
    }

    if (!data?.CI_Id) {
      navigate("/Candidates");
      return;
    }

    setPersonalDetails({
      Name: data?.CI_Name ?? "",
      Alias: data?.CI_Alias ?? "",
      Date_Of_Birth: data?.CI_Dateofbirth ?? null,
      Age: calculateAge(data?.CI_Dateofbirth),
      Passport_Number: data?.CI_PassportNo ?? "",
      Address: data?.CI_Address ?? "",
      Photo: data?.CI_Photo ?? "",
    });

    setExperienceDetails({
      Profession: data?.Applied_profession_Name ?? "",
      Is_Medical: data?.CI_IsMedical ? "Yes" : "No",
      Years_of_Experience: data?.CI_YearofExp ?? "",
      Career_Gap: data?.CI_CareerGap ?? "",
      Gap_Details: data?.CI_CareerGapDetl ?? "",
      Designation: data?.CI_Designation ?? "",
      Department_1: data?.CI_Department1 ?? "",
      Department_2: data?.CI_Department2 ?? "",
      Department_3: data?.CI_Department3 ?? "",
      Registration_License: data?.CI_Registration_License ?? "",
      Registration_Expiry: data?.CI_Registration_License_expry ?? null,
      last_Working_Date: data?.CI_Last_working_date ?? "",
    });

    const jobs = Array.isArray(data.applied_jobs) ? data.applied_jobs : [];

    setAppliedJobs(jobs);
    setCandidateId(data.CI_Id);
    setSelectedJobListId(jobs[0]?.JA_ApplicationId ?? null);
  }, [candidateData, navigate]);

  return (
    <Paper
      sx={{
        maxWidth: `100vw`,
        padding: 2,
        margin: "auto",
        boxShadow: "none",
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <CommonButton
          variant="outlined"
          background="#006CB8"
          onClick={() =>
            navigate(
              fromLocation == "Jobs" ? `/Candidates` : `/DeployedCandidates`,
              {
                state: {
                  pagination: paginationState,
                  filterData: filterData,
                },
              }
            )
          }
          margin="0px 20px 10px 0px"
        >
          <ArrowBackIcon sx={{ color: "white" }} />
        </CommonButton>

        <Typography
          variant="h5"
          sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
        >
          Candidate Details
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          sm={12}
          md={appliedJobs?.length > 0 ? 8 : 12}
          id="personalDetailsSection"
        >
          <Tabs
            value={selectedProfileTab}
            onChange={(e, value) => {
              setSelectedProfileTab(value);
            }}
            indicatorColor="error"
            textColor="error"
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              fontFamily: "Geist, sans-serif",
              "& .MuiTabs-flexContainer": { justifyContent: "flex-start" },
              borderRadius: "10px 0px 0px 0px",
            }}
          >
            {["Personal Details", "Experience Details"].map((label, index) => (
              <Tab
                key={label}
                label={label}
                sx={{
                  fontFamily: "Geist, sans-serif",
                  background:
                    selectedProfileTab !== index ? "#006CB8" : "white",
                  border: "1px solid lightgrey",
                  borderLeft:
                    index == 0
                      ? selectedProfileTab == index
                        ? "1px solid lightgrey"
                        : "0px"
                      : "0px",
                  borderBottom: "4px solid #EAB308",
                  color: selectedProfileTab !== index ? "white" : "black",
                  fontWeight: 500,
                  fontSize: `14px`,
                  letterSpacing: "1px",
                  minWidth: "150px",
                  minHeight: "60px",
                  borderRadius: index == 0 ? "10px 0px 0px 0px" : "",
                }}
              />
            ))}
          </Tabs>

          {selectedProfileTab == 0 && (
            <Box
              sx={{
                border: "2px solid lightgrey",
                p: 3,
                height: "calc(100vh - 300px)",
                overflowY: "auto",
              }}
            >
              {/* <Box
                sx={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
                >
                  Personal Details
                </Typography>
              </Box> */}

              <Avatar
                src={
                  personalDetails?.Photo
                    ? `${process.env.REACT_APP_BASE_URL}/${personalDetails.Photo}`
                    : undefined
                }
                sx={{
                  width: 140,
                  height: 140,
                  borderRadius: "50%",
                  border: "3px solid #ccc",
                  boxShadow: 2,
                  my: 3,
                  mx: "auto",
                }}
              >
                {personalDetails?.Name?.[0] || ""}
              </Avatar>

              <Grid container spacing={2}>
                {Object.entries(personalDetails)
                  ?.filter(([key]) => key !== "Photo")
                  ?.map(([key, value]) => (
                    <Grid item xs={12} sm={6} md={6} lg={6} xl={3} key={key}>
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "18px",
                            mb: 0.5,
                            fontFamily: "Geist, sans-serif",
                          }}
                        >
                          {key.replace(/_/g, " ")}
                        </Typography>
                        {value ? (
                          <Typography
                            sx={{
                              fontWeight: 400,
                              fontFamily: "Geist, sans-serif",
                              fontSize: "16px",

                              /* important for long text */
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                              whiteSpace: "normal",

                              /* optional safety */
                              maxWidth: "100%",
                            }}
                          >
                            {value}
                          </Typography>
                        ) : (
                          "-"
                        )}
                      </Box>
                    </Grid>
                  ))}
              </Grid>
            </Box>
          )}
          {selectedProfileTab == 1 && (
            <Box
              sx={{
                border: "2px solid lightgrey",
                p: 3,
                height: "calc(100vh - 300px)",
                overflowY: "auto",
              }}
            >
              {/* <Box
                sx={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
                >
                  Experience Details
                </Typography>
              </Box> */}

              <Grid container spacing={2}>
                {Object.entries(experienceDetails)
                  ?.filter(([key]) => key !== "Photo")
                  ?.map(([key, value]) => (
                    <Grid item xs={12} sm={6} md={6} lg={6} xl={4} key={key}>
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "18px",
                            mb: 0.5,
                            fontFamily: "Geist, sans-serif",
                          }}
                        >
                          {key.replace(/_/g, " ")}
                        </Typography>
                        {value ? (
                          <Typography
                            sx={{
                              fontWeight: 400,
                              fontFamily: "Geist, sans-serif",
                              fontSize: "16px",

                              /* important for long text */
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                              whiteSpace: "normal",

                              /* optional safety */
                              maxWidth: "100%",
                            }}
                          >
                            {value}
                          </Typography>
                        ) : (
                          "-"
                        )}
                      </Box>
                    </Grid>
                  ))}
              </Grid>
            </Box>
          )}
        </Grid>

        {appliedJobs?.length > 0 && (
          <>
            <Grid item xs={12} sm={12} md={4}>
              {/* Header */}
              <Box
                sx={{
                  minHeight: "60px",
                  background: "#006CB8",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  borderRadius:`5px 5px 0px 0px`
                }}
              >
                <Typography
                  fontWeight={600}
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: "0.5px",
                    fontSize: "16px",
                    color: "white",
                    whiteSpace: "nowrap",
                  }}
                >
                  Status of
                </Typography>

                <SelectInput
                  value={selectedJobListId}
                  onChange={(e) => handleChange(e.target.value)}
                  options={appliedJobs?.map((item) => ({
                    value: item?.JA_ApplicationId,
                    label: item?.job_applied,
                  }))}
                  placeholder="Select Job"
                />
              </Box>

              {/* Content */}
              <Box
                sx={{
                  height: "calc(100vh - 300px)",
                  overflowY: "auto",
                }}
              >
                <SideStageCard originalValue2={procedureData} mx="0" />
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    </Paper>
  );
};

export default ClientCandidateProfile;
